import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';
import { TEMPLATE_LIST_PAGE_SIZE } from '../constants/templateApi';
import { formatReportingDate } from '../constants/reportingConfig';

function buildListQuery(filters = {}) {
  const params = new URLSearchParams({
    next_page: String((filters.page ?? 0) + 1),
    search: filters.search ?? '',
    sort_field: filters.sortField ?? 'created_at',
    sort_order: filters.sortOrder ?? 'desc',
    category: filters.category ?? '',
    bot_number: filters.botNumber ?? '',
    limit: String(filters.limit ?? TEMPLATE_LIST_PAGE_SIZE),
    company_id: filters.organisation ?? '',
    status: filters.status ?? '',
    language: filters.language ?? '',
    archived: filters.archived ?? 'false',
    tag_id: filters.tag_id ?? '',
    start_date: formatReportingDate(filters.startDate) ?? '',
    end_date: formatReportingDate(filters.endDate) ?? '',
  });
  return params.toString();
}

export const templateService = {
  async getTemplates(filters = {}) {
    return APIService.get(`${Apis.getWhatsappTemplate}?${buildListQuery(filters)}`);
  },

  async getTemplateListCounts({ companyId, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const formattedStart = formatReportingDate(startDate);
    const formattedEnd = formatReportingDate(endDate);
    if (formattedStart) params.set('start_date', formattedStart);
    if (formattedEnd) params.set('end_date', formattedEnd);
    const query = params.toString();
    const url = query ? `${Apis.templateListCounts}?${query}` : Apis.templateListCounts;
    const res = await APIService.get(url);
    return res?.data?.data?.counts ?? res?.data?.counts ?? null;
  },

  async syncTemplates({ botNumber, companyId } = {}) {
    const params = new URLSearchParams();
    if (botNumber) params.set('bot_number', botNumber);
    if (companyId) params.set('company_id', companyId);
    const query = params.toString();
    const url = query ? `${Apis.syncWhatsappTemplates}?${query}` : Apis.syncWhatsappTemplates;
    const res = await APIService.post(url, {});
    return {
      status: res?.status,
      data: res?.data?.data ?? res?.data,
    };
  },

  async syncTemplate({ botNumber, name, language } = {}) {
    if (!botNumber || !name) {
      throw new Error('Bot number and template name are required');
    }
    const params = new URLSearchParams();
    params.set('bot_number', botNumber);
    params.set('name', name);
    if (language) params.set('language', language);
    const res = await APIService.post(`${Apis.syncWhatsappTemplates}?${params.toString()}`, {});
    return {
      status: res?.status,
      data: res?.data?.data ?? res?.data,
    };
  },

  async getTemplate(name, botNumber) {
    if (!name || !botNumber) throw new Error('Template name and bot number required');
    return APIService.get(`${Apis.whatsappTemplate}?bot_number=${botNumber}&name=${name}`);
  },

  async checkTemplateNameAvailability(name, botNumber) {
    if (!name?.trim() || !botNumber) {
      return { available: true, message: '' };
    }
    const params = new URLSearchParams({
      bot_number: botNumber,
      name: name.trim(),
    });
    const res = await APIService.get(`${Apis.checkWhatsappTemplateName}?${params.toString()}`);
    const data = res?.data?.data ?? res?.data ?? {};
    return {
      available: data.available !== false,
      message: data.message || '',
    };
  },

  async createTemplate(payload, botNumber) {
    return APIService.post(`${Apis.whatsappTemplate}?bot_number=${botNumber}`, payload);
  },

  async updateTemplate(payload) {
    return APIService.put(`${Apis.whatsappTemplate}?bot_number=${payload?.bot_number}`, payload);
  },

  async deleteTemplate(name, botNumber) {
    return APIService.remove(`${Apis.whatsappTemplate}?bot_number=${botNumber || ''}&name=${name}`);
  },

  async setTemplateArchiveStatus(name, botNumber, action) {
    const params = new URLSearchParams({
      bot_number: botNumber || '',
      name: name || '',
    });
    return APIService.patch(`${Apis.templateArchive}?${params}`, { action });
  },

  async archiveTemplate(name, botNumber) {
    return this.setTemplateArchiveStatus(name, botNumber, 'archive');
  },

  async restoreTemplate(name, botNumber) {
    return this.setTemplateArchiveStatus(name, botNumber, 'restore');
  },

  async getAllTemplates({ organisation_id, bot_number }) {
    return APIService.get(
      `${Apis.getWhatsappTemplate}?company_id=${organisation_id}&bot_number=${bot_number}&limit=500`
    );
  },

  async sendTemplate(payload, botNumber) {
    return APIService.post(`${Apis.sendWhatsappTemplate}${botNumber}`, payload);
  },

  async uploadRecipients(formData, { bot_number, wa_Id, scheduled_at } = {}, onUploadProgress) {
    const params = new URLSearchParams({
      bot_number: bot_number || '',
      id: wa_Id || '',
    });
    if (scheduled_at) params.set('scheduled_at', scheduled_at);
    return APIService.uploadFile(`${Apis.uploadUserData}?${params}`, formData, onUploadProgress);
  },

  async getTemplateQueue(filters = {}) {
    const params = new URLSearchParams({
      next_page: String((filters.page ?? 0) + 1),
      limit: String(filters.limit ?? TEMPLATE_LIST_PAGE_SIZE),
    });
    if (filters.organisation) params.set('company_id', filters.organisation);
    if (filters.botNumber) params.set('bot_number', filters.botNumber);
    if (filters.templateId) params.set('wa_id', filters.templateId);
    return APIService.get(`${Apis.templateQueue}?${params.toString()}`);
  },

  async getTemplateQueueItems(filters = {}) {
    const params = new URLSearchParams({
      next_page: String((filters.page ?? 0) + 1),
      limit: String(filters.limit ?? TEMPLATE_LIST_PAGE_SIZE),
      job_id: filters.jobId || '',
    });
    if (filters.destination) params.set('destination', filters.destination);
    if (filters.queueStatus) params.set('queue_status', filters.queueStatus);
    return APIService.get(`${Apis.templateQueueItems}?${params.toString()}`);
  },

  discardTemplateQueue(jobId) {
    return APIService.post(`${Apis.templateQueue}/${jobId}/discard`, {});
  },

  pauseTemplateQueue(jobId) {
    return APIService.post(`${Apis.templateQueue}/${jobId}/pause`, {});
  },

  resumeTemplateQueue(jobId) {
    return APIService.post(`${Apis.templateQueue}/${jobId}/resume`, {});
  },

  retryFailedTemplateQueue(jobId) {
    return APIService.post(`${Apis.templateQueue}/${jobId}/retry-failed`, {});
  },

  retryTemplateQueueItem(itemId) {
    return APIService.post(`${Apis.templateQueueItems}/${itemId}/retry`, {});
  },

  async getBulkQueueStatus(templateId) {
    return APIService.get(`${Apis.sendTemplateCronStatus}?template_id=${templateId}`);
  },

  async retryFailedBulkQueue(templateId) {
    return APIService.post(`${Apis.sendTemplateRetryFailed}?template_id=${templateId}`);
  },

  async bulkArchiveTemplates(items, action) {
    return APIService.patch(Apis.templateArchiveBulk, { action, items });
  },

  async getTemplateActivity(name, botNumber, limit = 50) {
    const params = new URLSearchParams({
      name: name || '',
      bot_number: botNumber || '',
      limit: String(limit),
    });
    return APIService.get(`${Apis.templateActivity}?${params}`);
  },

  async getOrgTemplateActivity(companyId, limit = 100) {
    const params = new URLSearchParams({
      company_id: companyId || '',
      limit: String(limit),
    });
    return APIService.get(`${Apis.templateActivity}?${params}`);
  },

  async getTemplateOrgSettings(companyId) {
    return APIService.get(`${Apis.templateOrgSettings}?company_id=${companyId || ''}`);
  },

  async getLastSyncMetadata(companyId) {
    const res = await this.getTemplateOrgSettings(companyId);
    return res;
  },

  async getTemplateTags(companyId) {
    const params = new URLSearchParams({ company_id: companyId || '' });
    return APIService.get(`${Apis.templateTags}?${params}`);
  },

  async createTemplateTag(name, companyId) {
    return APIService.post(Apis.templateTags, {
      name,
      company_id: companyId || '',
    });
  },

  async deleteTemplateTag(tagId, companyId) {
    const params = new URLSearchParams({
      tag_id: tagId || '',
      company_id: companyId || '',
    });
    return APIService.remove(`${Apis.templateTags}?${params}`);
  },

  async sendTemplateCron(templateId) {
    return APIService.post(`${Apis.sendTemplateCron}?template_id=${templateId}`);
  },

  async getBulkQueue(templateId) {
    return APIService.get(`${Apis.sendTemplateCron}?template_id=${templateId}`);
  },

  async clearBulkQueue(templateId) {
    return APIService.remove(`${Apis.sendTemplateCron}?template_id=${templateId}`);
  },

  async createLeanBulkSend(formData, { bot_number, wa_Id } = {}, onUploadProgress) {
    const params = new URLSearchParams({
      bot_number: bot_number || '',
      id: wa_Id || '',
    });
    return APIService.uploadFile(`${Apis.leanBulkSends}?${params}`, formData, onUploadProgress);
  },

  async startLeanBulkSend(jobId, { scheduled_at } = {}) {
    const params = new URLSearchParams();
    if (scheduled_at) params.set('scheduled_at', scheduled_at);
    const query = params.toString();
    const url = query
      ? `${Apis.leanBulkSends}/${jobId}/start?${query}`
      : `${Apis.leanBulkSends}/${jobId}/start`;
    return APIService.post(url);
  },

  async discardLeanBulkSend(jobId) {
    return APIService.remove(`${Apis.leanBulkSends}/${jobId}`);
  },

  async getLeanBulkSend(jobId) {
    return APIService.get(`${Apis.leanBulkSends}/${jobId}`);
  },

  async retryLeanBulkSend(jobId) {
    return APIService.post(`${Apis.leanBulkSends}/${jobId}/retry-failed`);
  },

  async uploadHeaderMedia(formData, botNumber) {
    return APIService.post(`${Apis.uploadHeaderFile}?bot_number=${botNumber}`, formData, {
      'Content-Type': 'multipart/form-data',
    });
  },

  async getDateWiseSummary(filters = {}) {
    /** @deprecated Use templateReportingService when USE_WHATSAPP_REPORTING is enabled */
    const params = new URLSearchParams({
      template_id: filters.template_id ?? '',
      start_date: filters.start_date ?? '',
      end_date: filters.end_date ?? '',
      next_page: String(filters.page ?? 0),
      sort_by: filters.sort_by ?? 'sent_date',
      order: filters.order ?? 'desc',
      limit: String(filters.limit ?? 10),
    });
    return APIService.get(`${Apis.getTemplateDateWiseSumnary}?${params}`);
  },

  /** @deprecated Use templateReportingService.getRecipients when USE_WHATSAPP_REPORTING is enabled. */
  async getUserSummary(filters = {}) {
    const params = new URLSearchParams({
      template_id: filters.templateId ?? '',
      date: filters.sentDate ?? '',
      next_page: String(filters.page ?? 0),
      search: filters.search ?? '',
      status: filters.status ?? '',
      assigned_to: filters.assigned_to ?? '',
      sort_by: filters.sortField ?? 'total_users',
      order: filters.sortOrder ?? 'desc',
      limit: String(filters.limit ?? 10),
      template_status: filters.template_status ?? '',
    });
    return APIService.get(`${Apis.getTemplateUserSummary}?${params}`);
  },

  async updateCallStatus(data) {
    return APIService.post(`${Apis.updateTemplateCallStatus}`, data);
  },

  async postUserComment(data) {
    return APIService.post(`${Apis.postTemplateUserComment}`, data);
  },

  async getWabaAccounts() {
    return APIService.get(Apis.getWhatsappBusinessInfo);
  },

  async createWabaApp(payload) {
    const res = await APIService.post(Apis.createWhatsappBusinessApp, payload, {
      'Content-Type': 'application/json',
    });
    return {
      status: res?.status,
      data: res?.data?.data ?? res?.data,
    };
  },

  async updateWabaApp(payload) {
    const res = await APIService.patch(Apis.updateWhatsappBusinessApp, payload, {
      'Content-Type': 'application/json',
    });
    return {
      status: res?.status,
      data: res?.data?.data ?? res?.data,
    };
  },

  async getBotNumbers(companyId) {
    if (!companyId) throw new Error('Company ID is required');
    return APIService.get(`${Apis.whatsappBusinessAccounts}?company_id=${companyId}`);
  },

  async registerWebhook(data) {
    return APIService.post(Apis.registerWhatsappWebhook, data, {
      'Content-Type': 'application/json',
    });
  },

  async getOptinList() {
    return APIService.get(Apis.getOptinList);
  },

  async downloadOptinList() {
    return APIService.downloadFile(Apis.optinDownload, `Optin_Users_${Date.now()}.xlsx`);
  },

  async optoutUser(phoneNumber) {
    if (!phoneNumber) throw new Error('Phone number is required');
    const cleaned = String(phoneNumber).trim().replace(/^\+/, '').replace(/\s+/g, '');
    return APIService.post(`${Apis.optoutUser}?phone_number=${encodeURIComponent(cleaned)}`, {
      phone_number: cleaned,
    });
  },

  // TrustSignal WABA & Health APIs
  async getTrustSignalPhoneHealth(wabaNumber, companyId) {
    const params = new URLSearchParams();
    if (wabaNumber) params.set('waba_number', String(wabaNumber).trim());
    if (companyId) params.set('company_id', String(companyId).trim());
    const query = params.toString();
    const url = query
      ? `${Apis.trustSignalWabaPhoneHealth}?${query}`
      : Apis.trustSignalWabaPhoneHealth;
    return APIService.get(url);
  },

  async getTrustSignalAllPhoneHealth(wabaIdOrParams, companyId) {
    let targetWabaId = wabaIdOrParams;
    let targetCompanyId = companyId;
    if (typeof wabaIdOrParams === 'object' && wabaIdOrParams !== null) {
      targetWabaId = wabaIdOrParams.waba_id || wabaIdOrParams.wabaId;
      targetCompanyId = wabaIdOrParams.company_id || wabaIdOrParams.companyId || companyId;
    }
    const params = new URLSearchParams();
    if (targetWabaId) params.set('waba_id', String(targetWabaId).trim());
    if (targetCompanyId) params.set('company_id', String(targetCompanyId).trim());
    const query = params.toString();
    const url = query
      ? `${Apis.trustSignalWabaAllPhoneHealth}?${query}`
      : Apis.trustSignalWabaAllPhoneHealth;
    return APIService.get(url);
  },

  async getTrustSignalTemplateHealth({ name, wabaNumber, companyId, templateId } = {}) {
    const params = new URLSearchParams();
    const effectiveTemplateId = templateId || name;
    if (effectiveTemplateId) params.set('template_id', String(effectiveTemplateId).trim());
    if (name) params.set('name', String(name).trim());
    if (wabaNumber) params.set('waba_number', String(wabaNumber).trim());
    if (companyId) params.set('company_id', String(companyId).trim());
    const query = params.toString();
    const url = query
      ? `${Apis.trustSignalWabaTemplateHealth}?${query}`
      : Apis.trustSignalWabaTemplateHealth;
    return APIService.get(url);
  },

  async getTrustSignalWabaMsgLimit(wabaId) {
    const params = new URLSearchParams();
    if (wabaId) params.set('waba_id', String(wabaId).trim());
    const query = params.toString();
    const url = query ? `${Apis.trustSignalWabaMsgLimit}?${query}` : Apis.trustSignalWabaMsgLimit;
    return APIService.get(url);
  },

  async triggerTrustSignalCallback(payload = {}) {
    return APIService.post(Apis.trustSignalCallback, payload);
  },
};
